import { useState, useRef, useEffect } from 'react'
import { supabase } from './supabase'
import LogoMark from './LogoMark'

// ─── Bot-signup defences (no external service required) ──────────────────────
// Three free, in-code protections — each catches a different bot pattern:
//
// 1. HONEYPOT — a hidden text field humans never see (and screen readers skip).
//    Naive scrapers fill every input on the page and trip on it.
//
// 2. TIME-TO-SUBMIT — humans take a few seconds to read labels, type a
//    password, and re-confirm it. Sub-2.5s submits are bots autofilling.
//
// 3. DISPOSABLE EMAIL BLOCKLIST — covers the most common throwaway providers
//    bot farms cycle through. Real users on these domains are vanishingly
//    rare for a paid product. Maintained list, easy to extend.
//
// For *serious* bot pressure, layer on Cloudflare Turnstile via Supabase's
// built-in CAPTCHA support (Auth → Settings → Bot and Abuse Protection).
// That requires a Cloudflare account (free) and Supabase keys — see replit.md.

const SIGNUP_MIN_DWELL_MS = 2500

const DISPOSABLE_EMAIL_DOMAINS = new Set([
  // High-volume throwaway services. Ordered alphabetically for easy diffing.
  '0-mail.com', '10minutemail.com', '20minutemail.com', '33mail.com',
  'anonbox.net', 'antichef.com', 'binkmail.com', 'bouncr.com', 'burnermail.io',
  'deadaddress.com', 'discard.email', 'disposable.com', 'disposablemail.com',
  'dispostable.com', 'dropmail.me', 'einrot.com', 'emailondeck.com',
  'emltmp.com', 'fake-email.pp.ua', 'fakeinbox.com', 'fakemailgenerator.com',
  'getairmail.com', 'getnada.com', 'guerrillamail.com', 'guerrillamail.net',
  'guerrillamail.org', 'harakirimail.com', 'inboxbear.com', 'inboxkitten.com',
  'jetable.org', 'mail-temp.com', 'mail.tm', 'mailbox.in.ua', 'maildrop.cc',
  'mailforspam.com', 'mailinator.com', 'mailnesia.com', 'mailsac.com',
  'mintemail.com', 'mohmal.com', 'moakt.com', 'mytemp.email', 'nada.email',
  'nowmymail.com', 'one-time.email', 'onetimeuse.email', 'opayq.com',
  'sharklasers.com', 'spam4.me', 'spambox.us', 'tempinbox.com', 'tempm.com',
  'tempmail.com', 'tempmail.net', 'tempmail.plus', 'tempmailaddress.com',
  'tempmailo.com', 'temp-mail.io', 'temp-mail.org', 'throwaway.email',
  'throwawaymail.com', 'tmail.io', 'tmpmail.net', 'tmpmail.org', 'trashmail.com',
  'trashmail.de', 'trashmail.io', 'trashmail.net', 'trashmail.ws',
  'wegwerfmail.de', 'yopmail.com', 'yopmail.fr', 'yopmail.net',
])

const isDisposableEmail = (email) => {
  // Case-insensitive: the original implementation lowercased only the lookup
  // string, then sliced from the original-case email — so `User@MAILINATOR.COM`
  // would bypass the blocklist. Always normalise the domain itself.
  const normalised = String(email || '').trim().toLowerCase().replace(/\.$/, '')
  const at = normalised.lastIndexOf('@')
  if (at < 0) return false
  const domain = normalised.slice(at + 1)
  if (!domain) return false
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) return true
  // Subdomain match: many providers spawn `*.tempmail.com`, `*.guerrillamail.com`
  // etc. as new throwaway hosts — block those too without enumerating each one.
  for (const blocked of DISPOSABLE_EMAIL_DOMAINS) {
    if (domain.endsWith(`.${blocked}`)) return true
  }
  return false
}

const ORANGE_GRADIENT = 'linear-gradient(135deg, #f97316, #dc2626)'
const BG = 'var(--bg)'
const CARD_BG = 'var(--card)'
const BORDER = 'var(--border-soft)'
const TEXT_STRONG = 'var(--text-strong)'
const TEXT_MUTED = 'var(--text-muted)'
const TEXT_DIM = 'var(--text-dim)'

const Spinner = ({ size = 16, color = '#fff' }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      border: `2px solid ${color}`,
      borderTop: '2px solid transparent',
      borderRadius: '50%',
      animation: 'authspin 0.8s linear infinite',
    }}
  />
)

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.7-11.3 7.7-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.2 5.5 29.4 3.5 24 3.5 12.7 3.5 3.5 12.7 3.5 24S12.7 44.5 24 44.5c11 0 20-8 20-20.5 0-1.4-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.2 6.5 29.4 4.5 24 4.5 16.3 4.5 9.6 8.7 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44.5c5.3 0 10-2 13.5-5.3l-6.2-5.1c-2 1.4-4.5 2.3-7.3 2.3-5.4 0-9.7-3.2-11.3-7.6l-6.5 5C9.5 40.2 16.2 44.5 24 44.5z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.2 5.1c-.4.4 6.5-4.7 6.5-14.4 0-1.4-.1-2.4-.4-3.5z" />
  </svg>
)

const Field = ({ label, ...props }) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <span
      style={{
        display: 'block',
        color: TEXT_MUTED,
        fontSize: 12,
        marginBottom: 6,
        fontFamily: 'sans-serif',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
    <input
      {...props}
      style={{
        width: '100%',
        padding: '12px 14px',
        borderRadius: 10,
        background: 'var(--input-bg)',
        border: `1px solid ${BORDER}`,
        color: TEXT_STRONG,
        fontSize: 14,
        outline: 'none',
        fontFamily: 'sans-serif',
        boxSizing: 'border-box',
      }}
    />
  </label>
)

const PrimaryButton = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading || props.disabled}
    style={{
      width: '100%',
      padding: '13px 16px',
      borderRadius: 11,
      background: ORANGE_GRADIENT,
      border: 'none',
      color: '#fff',
      fontSize: 15,
      fontWeight: 700,
      cursor: loading ? 'not-allowed' : 'pointer',
      fontFamily: 'sans-serif',
      opacity: loading ? 0.75 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      boxShadow: '0 6px 20px rgba(249,115,22,0.3)',
    }}
  >
    {loading && <Spinner />}
    {children}
  </button>
)

const SecondaryButton = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading || props.disabled}
    style={{
      width: '100%',
      padding: '12px 16px',
      borderRadius: 11,
      background: 'var(--surface-2)',
      border: `1px solid ${BORDER}`,
      color: TEXT_STRONG,
      fontSize: 14,
      fontWeight: 600,
      cursor: loading ? 'not-allowed' : 'pointer',
      fontFamily: 'sans-serif',
      opacity: loading ? 0.75 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
    }}
  >
    {loading ? <Spinner color={TEXT_STRONG} /> : <GoogleIcon />}
    {children}
  </button>
)

const Divider = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      margin: '18px 0',
      color: TEXT_DIM,
      fontSize: 11,
      fontFamily: 'sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 1.2,
    }}
  >
    <span style={{ flex: 1, height: 1, background: BORDER }} />
    or
    <span style={{ flex: 1, height: 1, background: BORDER }} />
  </div>
)

const LinkButton = ({ children, ...props }) => (
  <button
    {...props}
    style={{
      background: 'none',
      border: 'none',
      color: '#f97316',
      fontSize: 13,
      cursor: 'pointer',
      fontFamily: 'sans-serif',
      fontWeight: 600,
      padding: 0,
    }}
  >
    {children}
  </button>
)

const Logo = () => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: '0 auto 14px' }}>
    <LogoMark size={64} />
  </div>
)

export default function AuthScreen({ initialView = 'login', onClose, onViewPolicy }) {
  const [view, setView] = useState(initialView) // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Anti-bot — honeypot value (real users never type into it; it's hidden) and
  // a render timestamp so we can require a minimum dwell time on signup.
  const [honeypot, setHoneypot] = useState('')
  const signupRenderedAtRef = useRef(0)
  useEffect(() => {
    if (view === 'signup') {
      signupRenderedAtRef.current = Date.now()
    }
  }, [view])

  const switchView = (next) => {
    setError('')
    setSuccess('')
    setHoneypot('')
    setView(next)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // ── Bot defences run BEFORE we hit Supabase ──────────────────────────────
    // 1. Honeypot — bots fill every visible-looking input. Fail silently with
    //    a generic-sounding error so we don't tip off the bot.
    if (honeypot && honeypot.trim() !== '') {
      setError('Something went wrong. Please refresh and try again.')
      return
    }
    // 2. Dwell time — humans cannot read three labels, type a real password
    //    twice, and hit submit in under ~2.5 seconds. Bots autofilling can.
    const dwellMs = Date.now() - (signupRenderedAtRef.current || 0)
    if (dwellMs < SIGNUP_MIN_DWELL_MS) {
      setError('Please take a moment to review your details before submitting.')
      return
    }
    // 3. Disposable-email blocklist — these domains are almost exclusively
    //    used to bypass email verification on free signups.
    if (isDisposableEmail(email)) {
      setError('Please sign up with a permanent email address — disposable inboxes are not supported.')
      return
    }
    // ─────────────────────────────────────────────────────────────────────────

    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setLoading(true)
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) setError(error.message)
    else setSuccess('Check your email to confirm your account!')
    setLoading(false)
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    if (error) setError(error.message)
    else setSuccess('Password reset link sent — check your email!')
    setLoading(false)
  }

  const handleGoogleLogin = async () => {
    setError('')
    setSuccess('')
    setGoogleLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        // Force Google's account chooser every time. Without this, mobile
        // browsers silently sign in with the device's active Google account
        // (single account on the device → no picker shown). Desktop usually
        // shows the picker by default because more users are signed into
        // multiple accounts in the browser. `select_account` makes the
        // behavior consistent across platforms.
        queryParams: { prompt: 'select_account' },
      },
    })
    if (error) setError(error.message)
    setGoogleLoading(false)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: "'Lora', serif",
      }}
    >
      <style>{`@keyframes authspin { to { transform: rotate(360deg); } }`}</style>

      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: CARD_BG,
          border: `1px solid ${BORDER}`,
          borderRadius: 22,
          padding: 30,
          backdropFilter: 'blur(16px)',
          position: 'relative',
        }}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to home"
            style={{
              position: 'absolute',
              top: 14,
              left: 14,
              background: 'var(--surface-2)',
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              color: TEXT_MUTED,
              fontSize: 12,
              padding: '5px 10px',
              cursor: 'pointer',
              fontFamily: 'sans-serif',
              fontWeight: 600,
            }}
          >
            ← Home
          </button>
        )}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <Logo />
          <h1
            style={{
              color: TEXT_STRONG,
              fontSize: 22,
              margin: '0 0 4px',
              fontWeight: 700,
              letterSpacing: -0.3,
            }}
          >
            {view === 'login' && 'Welcome back'}
            {view === 'signup' && 'Create your account'}
            {view === 'forgot' && 'Reset your password'}
          </h1>
          <p
            style={{
              color: TEXT_DIM,
              fontSize: 13,
              margin: 0,
              fontFamily: 'sans-serif',
            }}
          >
            {view === 'login' && 'Sign in to continue practicing.'}
            {view === 'signup' && 'Free to start. No credit card required.'}
            {view === 'forgot' && "We'll email you a link to reset it."}
          </p>
        </div>

        {view === 'login' && (
          <form onSubmit={handleLogin}>
            <Field
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <Field
              label="Password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 14 }}>
              <LinkButton type="button" onClick={() => switchView('forgot')}>
                Forgot password?
              </LinkButton>
            </div>
            <PrimaryButton type="submit" loading={loading}>
              Sign in
            </PrimaryButton>
          </form>
        )}

        {view === 'signup' && (
          <form onSubmit={handleSignup}>
            {/*
              Honeypot field. Visually hidden, off-screen, tab-skipped, and
              marked aria-hidden + autocomplete=off so assistive tech and
              password managers leave it alone. Bots that fill every input
              will trip on it. We use a plausible-sounding name ("company")
              because dumb bots fingerprint on common field names.
            */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
                clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
              }}
            >
              <label htmlFor="company">Company (leave blank)</label>
              <input
                type="text"
                id="company"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>
            <Field
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <Field
              label="Password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />
            <Field
              label="Confirm password"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            <PrimaryButton type="submit" loading={loading}>
              Create account
            </PrimaryButton>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleForgot}>
            <Field
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <PrimaryButton type="submit" loading={loading}>
              Send reset link
            </PrimaryButton>
          </form>
        )}

        {error && (
          <p
            style={{
              color: '#f87171',
              fontSize: 13,
              marginTop: 14,
              textAlign: 'center',
              fontFamily: 'sans-serif',
            }}
          >
            {error}
          </p>
        )}
        {success && (
          <p
            style={{
              color: '#4ade80',
              fontSize: 13,
              marginTop: 14,
              textAlign: 'center',
              fontFamily: 'sans-serif',
            }}
          >
            {success}
          </p>
        )}

        {view !== 'forgot' && (
          <>
            <Divider />
            <SecondaryButton
              type="button"
              loading={googleLoading}
              onClick={handleGoogleLogin}
            >
              Continue with Google
            </SecondaryButton>
          </>
        )}

        <div
          style={{
            marginTop: 22,
            textAlign: 'center',
            color: TEXT_DIM,
            fontSize: 13,
            fontFamily: 'sans-serif',
          }}
        >
          {view === 'login' && (
            <>
              No account?{' '}
              <LinkButton type="button" onClick={() => switchView('signup')}>
                Sign up free
              </LinkButton>
            </>
          )}
          {view === 'signup' && (
            <>
              Already have an account?{' '}
              <LinkButton type="button" onClick={() => switchView('login')}>
                Sign in
              </LinkButton>
            </>
          )}
          {view === 'forgot' && (
            <LinkButton type="button" onClick={() => switchView('login')}>
              ← Back to sign in
            </LinkButton>
          )}
        </div>

        {view !== 'forgot' && (
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: `1px solid ${BORDER}`,
              color: TEXT_DIM,
              fontSize: 11,
              lineHeight: 1.55,
              fontFamily: 'sans-serif',
              textAlign: 'center',
              opacity: 0.85,
            }}
          >
            {view === 'signup' && onViewPolicy && (
              <p style={{ margin: '0 0 8px' }}>
                By creating an account you agree to our{' '}
                <button type="button" onClick={() => onViewPolicy('terms')} style={{ background:'none', border:'none', padding:0, color:'#f97316', textDecoration:'underline', cursor:'pointer', font:'inherit' }}>Terms</button>,{' '}
                <button type="button" onClick={() => onViewPolicy('privacy')} style={{ background:'none', border:'none', padding:0, color:'#f97316', textDecoration:'underline', cursor:'pointer', font:'inherit' }}>Privacy Policy</button>,{' '}
                <button type="button" onClick={() => onViewPolicy('cookies')} style={{ background:'none', border:'none', padding:0, color:'#f97316', textDecoration:'underline', cursor:'pointer', font:'inherit' }}>Cookie Policy</button>, and{' '}
                <button type="button" onClick={() => onViewPolicy('disclaimer')} style={{ background:'none', border:'none', padding:0, color:'#f97316', textDecoration:'underline', cursor:'pointer', font:'inherit' }}>Disclaimer</button>.
              </p>
            )}
            <p style={{ margin: 0, fontStyle: 'italic' }}>
              {view === 'signup'
                ? 'This is an AI practice tool, not a guarantee of employment outcomes.'
                : 'This is an AI practice tool — not a guarantee of employment outcomes.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
